import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Heading,
  Table,
  TextField,
  TextArea,
  Dialog,
  Select,
  Badge,
  AlertDialog
} from '@radix-ui/themes';
import { PlusIcon, ReloadIcon, Pencil1Icon, ArchiveIcon } from '@radix-ui/react-icons';
import { toast } from 'react-hot-toast';
import { Switch } from '@radix-ui/themes';
import AdminLayout from './AdminLayout';
import { adminApi, organizationApi, customViewsApi } from '../../services/api';
import { useOrganization } from '../../contexts/OrganizationContext';

interface ClinicFormData {
  name: string;
  domain?: string;
  logoPath?: string;
  voicePhoneNumber?: string;
  smsPhoneNumber?: string;
  // PHA-1202: which provider sends this clinic's SMS; smsPhoneNumber must belong to it.
  smsProvider?: SmsProvider;
  // PHA-1202: the RingCentral account (entry of the prod/sms/ringcentral secret) a
  // RingCentral clinic sends with — the SMS counterpart of cobaltCredentialKey.
  ringcentralCredentialKey?: string;
  smsMessageTemplate?: string;
  tellihealthWebhookToken?: string;
  ownerUserId?: string;
  voiceEnabled?: boolean;
  welcomeSmsEnabled?: boolean;
  welcomeSmsTemplate?: string;
  voiceOfPatientEnabled?: boolean;
  noTargetDatesEnabled?: boolean;
  hideSmartHubEnabled?: boolean;
  // PHA-1184: per-clinic AI insights rollout (organizations.smart_hub_ai_enabled).
  smartHubAiEnabled?: boolean;
  // PHA-1080: care plan review cycle in months. Held as a STRING, not a number, because it
  // is bound to a text input: a number-typed field has no way to represent the half-typed
  // and the cleared states, and `Number('')` is 0 -- which is the clear-to-NULL sentinel, so
  // an empty field would read as a deliberate "go back to inheriting" on every keystroke.
  // Converted once, on submit.
  carePlanReviewMonths?: string;
  // PHA-958: EHR integration. ehrIntegration is the on/off gate; ehrProvider names the
  // EHR; cobaltCredentialKey maps an eCW clinic to an entry of the shared
  // COBALT_CREDENTIALS secret; runNightlyImport enrolls it in the daily EHR refresh.
  ehrIntegration?: boolean;
  ehrProvider?: EhrProvider | '';
  cobaltCredentialKey?: string;
  runNightlyImport?: boolean;
  // PHA-983: care-plan push to the EHR. Separate opt-in from ehrIntegration — a clinic can
  // import charts long before it agrees to us writing documents into them.
  ehrCarePlanPushEnabled?: boolean;
  // PHA-1204: also push automatically when a new care plan is approved.
  ehrCarePlanAutoPushEnabled?: boolean;
  ehrDocumentFolderName?: string;
  ehrDocumentLocationId?: string;
  ehrDocumentAssignedToId?: string;
  ehrDocumentIsReviewed?: boolean;
  ehrDocumentHighPriority?: boolean;
  ehrDocumentTags?: string;
  // PHA-1104: billing-report push. Its own opt-in and its own folder — a monthly billing
  // audit trail is not a clinical document, and clinics file the two in different places
  // (PHA-1105). Everything below the folder is shared with the care-plan push.
  ehrBillingReportPushEnabled?: boolean;
  ehrBillingReportFolderName?: string;
}

type EhrProvider = 'athena' | 'ecw';

// PHA-1202: per-clinic SMS provider (organizations.sms_provider).
type SmsProvider = 'twilio' | 'ringcentral';

const SMS_PROVIDER_OPTIONS: { value: SmsProvider; label: string }[] = [
  { value: 'twilio', label: 'Twilio' },
  { value: 'ringcentral', label: 'RingCentral' },
];

// PHA-983: the folder new clinics start with. Cobalt requires the folder to ALREADY EXIST
// in the EHR — it will not create one — so this must be a plain, generic name a clinic is
// likely to already have, not something we invented. The "- Phanova" suffix a prior version
// of this default used doesn't exist in any real eCW instance and made every push fail with
// a "folder not found" error, including every re-send (see PHA-983 Tom's report). Editable
// per clinic; this is only the starting point.
const DEFAULT_EHR_DOCUMENT_FOLDER = 'Treatment Plan Documents';

const EHR_PROVIDER_OPTIONS: { value: EhrProvider; label: string }[] = [
  { value: 'athena', label: 'Athena' },
  { value: 'ecw', label: 'eClinicalWorks' },
];

interface AdminOrganization {
  id: string;
  name: string;
  domain?: string | null;
  logoPath?: string | null;
  voicePhoneNumber?: string | null;
  smsPhoneNumber?: string | null;
  smsProvider?: SmsProvider | null;
  ringcentralCredentialKey?: string | null;
  smsMessageTemplate?: string | null;
  tellihealthWebhookToken?: string | null;
  ownerEmail?: string | null;
  ownerUserId?: string | null;
  userCount?: number | null;
  patientCount?: number | null;
  ehrIntegration?: boolean | null;
  // PHA-958: ehrIntegration above is the INHERITED value the list Badge shows (an org
  // under an EHR-integrated parent reads true). The edit form must bind to the org's OWN
  // row, or saving would copy a parent's setting down onto the child.
  ehrIntegrationOwn?: boolean | null;
  ehrProvider?: EhrProvider | null;
  cobaltCredentialKey?: string | null;
  runNightlyImport?: boolean | null;
  ehrCarePlanPushEnabled?: boolean | null;
  ehrCarePlanAutoPushEnabled?: boolean | null;
  ehrDocumentFolderName?: string | null;
  ehrDocumentLocationId?: string | null;
  ehrDocumentAssignedToId?: string | null;
  ehrDocumentIsReviewed?: boolean | null;
  ehrDocumentHighPriority?: boolean | null;
  ehrDocumentTags?: string[] | null;
  ehrBillingReportPushEnabled?: boolean | null;
  ehrBillingReportFolderName?: string | null;
  voiceEnabled?: boolean | null;
  welcomeSmsEnabled?: boolean | null;
  welcomeSmsTemplate?: string | null;
  voiceOfPatientEnabled?: boolean | null;
  noTargetDatesEnabled?: boolean | null;
  hideSmartHubEnabled?: boolean | null;
  smartHubAiEnabled?: boolean | null;
  // PHA-1080: the org's OWN value (null = inherits from its parent network). The list query
  // returns the own-row value on purpose -- binding the edit form to the resolved/inherited
  // one would turn "inherit" into a hard pin the first time anybody saved the clinic.
  carePlanReviewMonths?: number | null;
  archived?: boolean | null;
}

interface AdminUserOption {
  id: string;
  email: string;
  name?: string | null;
}

const normalizeOrganizations = (data: any): AdminOrganization[] => {
  const records = data?.records || data?.organizations || data || [];
  if (!Array.isArray(records)) return [];
  return records.map((record: any) => ({
    id: String(record.id ?? record.organization_id ?? record.organizationId ?? record.org_id),
    name: record.name ?? record.organization_name ?? 'Unnamed Clinic',
    domain: record.domain ?? record.website_domain ?? record.domain_name ?? null,
    logoPath: record.logo_path ?? record.logoPath ?? null,
    voicePhoneNumber: record.voice_phone_number ?? record.voicePhoneNumber ?? null,
    smsPhoneNumber: record.sms_phone_number ?? record.smsPhoneNumber ?? null,
    smsProvider: (record.sms_provider ?? record.smsProvider ?? 'twilio') as SmsProvider,
    ringcentralCredentialKey: record.ringcentral_credential_key ?? record.ringcentralCredentialKey ?? null,
    smsMessageTemplate: record.sms_message_template ?? record.smsMessageTemplate ?? null,
    tellihealthWebhookToken: record.tellihealth_webhook_token ?? record.tellihealthWebhookToken ?? null,
    ownerEmail: record.owner_email ?? record.ownerEmail ?? record.owner_user_email ?? record.owner ?? null,
    ownerUserId: record.owner_user_id ?? record.ownerUserId ?? record.owner_id ?? null,
    userCount: record.user_count ?? record.userCount ?? record.users_count ?? null,
    patientCount: record.patient_count ?? record.patientCount ?? record.patients_count ?? null,
    ehrIntegration: record.ehr_integration ?? null,
    ehrIntegrationOwn: record.ehr_integration_own ?? record.ehrIntegrationOwn ?? null,
    ehrProvider: (record.ehr_provider ?? record.ehrProvider ?? null) as EhrProvider | null,
    cobaltCredentialKey: record.cobalt_credential_key ?? record.cobaltCredentialKey ?? null,
    runNightlyImport: record.run_nightly_import ?? record.runNightlyImport ?? false,
    ehrCarePlanPushEnabled: record.ehr_care_plan_push_enabled ?? record.ehrCarePlanPushEnabled ?? false,
    ehrCarePlanAutoPushEnabled:
      record.ehr_care_plan_auto_push_enabled ?? record.ehrCarePlanAutoPushEnabled ?? false,
    ehrDocumentFolderName: record.ehr_document_folder_name ?? record.ehrDocumentFolderName ?? null,
    ehrDocumentLocationId: record.ehr_document_location_id ?? record.ehrDocumentLocationId ?? null,
    ehrDocumentAssignedToId: record.ehr_document_assigned_to_id ?? record.ehrDocumentAssignedToId ?? null,
    ehrDocumentIsReviewed: record.ehr_document_is_reviewed ?? record.ehrDocumentIsReviewed ?? false,
    ehrDocumentHighPriority: record.ehr_document_high_priority ?? record.ehrDocumentHighPriority ?? false,
    ehrDocumentTags: record.ehr_document_tags ?? record.ehrDocumentTags ?? null,
    ehrBillingReportPushEnabled:
      record.ehr_billing_report_push_enabled ?? record.ehrBillingReportPushEnabled ?? false,
    ehrBillingReportFolderName:
      record.ehr_billing_report_folder_name ?? record.ehrBillingReportFolderName ?? null,
    voiceEnabled: record.voice_enabled ?? record.voiceEnabled ?? false,
    welcomeSmsEnabled: record.welcome_sms_enabled ?? record.welcomeSmsEnabled ?? false,
    welcomeSmsTemplate: record.welcome_sms_template ?? record.welcomeSmsTemplate ?? null,
    voiceOfPatientEnabled: record.voice_of_patient_enabled ?? record.voiceOfPatientEnabled ?? false,
    noTargetDatesEnabled: record.no_target_dates_enabled ?? record.noTargetDatesEnabled ?? false,
    // PHA-1080: `?? null`, never `?? 6`. null is meaningful here -- it is "this clinic
    // inherits" -- and defaulting it to 6 would render an explicit 6 in the form, which on
    // the next save would write one and break inheritance for the whole network.
    carePlanReviewMonths: record.care_plan_review_months ?? record.carePlanReviewMonths ?? null,
    hideSmartHubEnabled: record.hide_smart_hub_enabled ?? record.hideSmartHubEnabled ?? false,
    smartHubAiEnabled: record.smart_hub_ai_enabled ?? record.smartHubAiEnabled ?? false,
    archived: record.archived ?? record.is_archived ?? null,
  }));
};


// PHA-1168: the Custom Views rollout switch for one clinic. Enabling a network
// enables every clinic under it (resolved ancestor-aware on the server).
const CustomViewsToggle: React.FC<{ organizationId: string }> = ({ organizationId }) => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let cancelled = false;
    customViewsApi.enabledOrgs()
      .then((r) => { if (!cancelled) setEnabled(r.organization_ids.includes(organizationId)); })
      .catch(() => { if (!cancelled) setEnabled(null); });
    return () => { cancelled = true; };
  }, [organizationId]);
  return (
    <Box>
      <Text size="2" weight="medium" className="mb-1 block">Custom Views</Text>
      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled === true}
          disabled={enabled === null || saving}
          onChange={async (e) => {
            const next = e.target.checked;
            setSaving(true);
            try {
              await customViewsApi.setEnabled(organizationId, next);
              setEnabled(next);
              toast.success(next ? 'Custom views enabled' : 'Custom views disabled');
            } catch (err: any) {
              toast.error(err?.message || 'Could not change custom views');
            } finally {
              setSaving(false);
            }
          }}
          className="w-4 h-4"
        />
        <Text size="2">Let this clinic's admins design Patient List and Billing Report views with AI.</Text>
      </label>
      <Text size="1" color="gray" className="mt-1 block">
        Saves immediately. Enabling a network enables all of its clinics. {enabled === null ? 'Unavailable until the PHA-1168 migration is applied.' : ''}
      </Text>
    </Box>
  );
};

const AdminClinicsPage: React.FC = () => {
  const [clinics, setClinics] = useState<AdminOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // PHA-527: org-state from OrganizationContext.
  const { orgId: ctxOrgId } = useOrganization();
  const sessionOrgId: string | null = ctxOrgId || null;

  const [formOpen, setFormOpen] = useState(false);
  const [editingClinic, setEditingClinic] = useState<AdminOrganization | null>(null);
  const [formData, setFormData] = useState<ClinicFormData>({ name: '' });
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const [archiveTarget, setArchiveTarget] = useState<AdminOrganization | null>(null);
  const [users, setUsers] = useState<AdminUserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Language support
  const ALL_LANGUAGES = [
    { code: 'es-US', name: 'Spanish', native_name: 'Español' },
    { code: 'fr-FR', name: 'French', native_name: 'Français' },
    { code: 'he-IL', name: 'Hebrew', native_name: 'עברית' },
    { code: 'ar-001', name: 'Arabic', native_name: 'العربية' },
  ];
  const [orgLanguages, setOrgLanguages] = useState<string[]>([]);
  const [langTemplates, setLangTemplates] = useState<Record<string, string>>({});

  // PHA-723: per-clinic supported programs (APCM / CCM / RPM). A clinic bills and
  // displays only the programs enabled here.
  const ALL_PROGRAMS = [
    { code: 'APCM', name: 'APCM', description: 'Advanced Primary Care Management' },
    { code: 'CCM', name: 'CCM', description: 'Chronic Care Management' },
    { code: 'RPM', name: 'RPM', description: 'Remote Patient Monitoring' },
  ];
  const [orgPrograms, setOrgPrograms] = useState<string[]>([]);
  // PHA-991: true when the clinic has no explicit config of its own and these toggles
  // are showing the effective set it inherits (parent network, else its default_program).
  // Flipping any toggle persists the current selection, so the screen and billing agree.
  const [orgProgramsInherited, setOrgProgramsInherited] = useState(false);

  // PHA-958: the COBALT_CREDENTIALS keys an eCW clinic can be mapped to. Names only —
  // the backend never sends the credential bodies. Fetched lazily, the first time eCW is
  // selected, so opening the dialog for an Athena clinic costs no Secrets Manager read.
  // Each Cobalt account is bound to ONE EHR on Cobalt's side. `ehr` is null when the
  // secret does not record it — render that as unknown rather than guessing.
  const [cobaltKeys, setCobaltKeys] = useState<{ key: string; ehr: string | null }[]>([]);
  const [cobaltKeysLoading, setCobaltKeysLoading] = useState(false);
  const [cobaltKeysLoaded, setCobaltKeysLoaded] = useState(false);
  const [cobaltKeysError, setCobaltKeysError] = useState<string | null>(null);

  // PHA-983: live Cobalt lookups that back the location / assigned-to pickers. Keyed by
  // credential key, because the roster belongs to the Cobalt ACCOUNT rather than to our
  // clinic record — switching the credential must invalidate both lists.
  const [cobaltLocations, setCobaltLocations] = useState<{ id: string; ehr_id?: string; name: string; status?: string }[]>([]);
  const [cobaltProviders, setCobaltProviders] = useState<{ id: string; ehr_id?: string; name: string; status?: string }[]>([]);
  const [cobaltLookupLoading, setCobaltLookupLoading] = useState(false);
  const [cobaltLookupError, setCobaltLookupError] = useState<string | null>(null);

  const loadCobaltDirectory = useCallback(async (credentialKey: string) => {
    if (!credentialKey) {
      setCobaltLocations([]);
      setCobaltProviders([]);
      return;
    }
    setCobaltLookupLoading(true);
    setCobaltLookupError(null);
    try {
      const [locs, provs] = await Promise.all([
        adminApi.listCobaltLocations(credentialKey),
        adminApi.listCobaltProviders(credentialKey),
      ]);
      setCobaltLocations(Array.isArray(locs?.locations) ? locs.locations : []);
      // Deliberately unfiltered by status: Diamond Medical's entire provider roster reads
      // 'inactive', so an active-only filter would render an empty picker on a working
      // account with nothing to explain why.
      setCobaltProviders(Array.isArray(provs?.providers) ? provs.providers : []);
    } catch (err: any) {
      setCobaltLookupError(err?.message || 'Could not read the EHR directory from Cobalt');
      setCobaltLocations([]);
      setCobaltProviders([]);
    } finally {
      setCobaltLookupLoading(false);
    }
  }, []);

  const loadCobaltKeys = useCallback(async () => {
    if (cobaltKeysLoaded || cobaltKeysLoading) return;
    setCobaltKeysLoading(true);
    setCobaltKeysError(null);
    try {
      const data = await adminApi.listCobaltCredentialKeys();
      // Prefer `accounts` (key + ehr); fall back to bare `keys` from an older backend.
      if (Array.isArray(data?.accounts)) {
        setCobaltKeys(data.accounts.map((a: any) => ({ key: String(a.key), ehr: a.ehr ?? null })));
      } else if (Array.isArray(data?.keys)) {
        setCobaltKeys(data.keys.map((k: any) => ({ key: String(k), ehr: null })));
      } else {
        setCobaltKeys([]);
      }
      // The backend answers 200 with {keys: [], error} when the secret is unreadable, so
      // an empty dropdown can be explained rather than looking like "no clinics exist".
      if (data?.error) setCobaltKeysError(String(data.error));
      setCobaltKeysLoaded(true);
    } catch (err: any) {
      setCobaltKeysError(err?.message || 'Failed to load Cobalt credentials');
    } finally {
      setCobaltKeysLoading(false);
    }
  }, [cobaltKeysLoaded, cobaltKeysLoading]);

  // PHA-1202: RingCentral accounts (names only, from the prod/sms/ringcentral secret) and
  // the SMS-enabled numbers of the selected one. Fetched lazily, like the Cobalt keys. An
  // account whose secret entry still holds placeholders reads `configured: false`.
  const [rcAccounts, setRcAccounts] = useState<{ key: string; configured: boolean }[]>([]);
  const [rcAccountsLoading, setRcAccountsLoading] = useState(false);
  const [rcAccountsLoaded, setRcAccountsLoaded] = useState(false);
  const [rcAccountsError, setRcAccountsError] = useState<string | null>(null);
  const [rcNumbers, setRcNumbers] = useState<{ phone_number: string; usage_type?: string | null }[]>([]);
  const [rcNumbersLoading, setRcNumbersLoading] = useState(false);
  const [rcNumbersError, setRcNumbersError] = useState<string | null>(null);

  const loadRcAccounts = useCallback(async () => {
    if (rcAccountsLoaded || rcAccountsLoading) return;
    setRcAccountsLoading(true);
    setRcAccountsError(null);
    try {
      const data = await adminApi.listRingCentralCredentialKeys();
      setRcAccounts(Array.isArray(data?.accounts)
        ? data.accounts.map((a: any) => ({ key: String(a.key), configured: Boolean(a.configured) }))
        : []);
      if (data?.error) setRcAccountsError(String(data.error));
      setRcAccountsLoaded(true);
    } catch (err: any) {
      setRcAccountsError(err?.message || 'Failed to load RingCentral accounts');
    } finally {
      setRcAccountsLoading(false);
    }
  }, [rcAccountsLoaded, rcAccountsLoading]);

  const loadRcNumbers = useCallback(async (credentialKey: string) => {
    if (!credentialKey) {
      setRcNumbers([]);
      return;
    }
    setRcNumbersLoading(true);
    setRcNumbersError(null);
    try {
      const data = await adminApi.listRingCentralSmsNumbers(credentialKey);
      setRcNumbers(Array.isArray(data?.numbers) ? data.numbers : []);
      if (data?.error) setRcNumbersError(String(data.error));
    } catch (err: any) {
      setRcNumbersError(err?.message || 'Could not read the RingCentral account');
      setRcNumbers([]);
    } finally {
      setRcNumbersLoading(false);
    }
  }, []);

  const loadClinics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getOrganizations();
      setClinics(normalizeOrganizations(data));
    } catch (err: any) {
      console.error('Failed to load clinics:', err);
      setError(err.message || 'Failed to load clinics');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    if (usersLoading || users.length > 0) return;
    setUsersLoading(true);
    try {
      const data = await adminApi.getUsers();
      const records = data?.records || data?.users || data || [];
      const normalized = Array.isArray(records)
        ? records.map((record: any) => ({
            id: String(record.id ?? record.user_id ?? record.userId ?? record.sub),
            email: record.email ?? record.user_email ?? 'unknown',
            name: record.name ?? record.full_name ?? null,
          }))
        : [];
      setUsers(normalized);
    } catch (err: any) {
      console.error('Failed to load users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [usersLoading, users.length]);

  useEffect(() => {
    loadClinics();
  }, [loadClinics]);

  const filteredClinics = useMemo(() => {
    const scoped = !sessionOrgId || sessionOrgId === 'ALL'
      ? clinics
      : clinics.filter((clinic) => clinic.id === sessionOrgId);
    const query = search.trim().toLowerCase();
    if (!query) return scoped;
    return scoped.filter((clinic) => {
      const name = clinic.name?.toLowerCase() || '';
      const domain = clinic.domain?.toLowerCase() || '';
      const owner = clinic.ownerEmail?.toLowerCase() || '';
      return name.includes(query) || domain.includes(query) || owner.includes(query);
    });
  }, [clinics, search, sessionOrgId]);

  const resetForm = () => {
    setEditingClinic(null);
    setFormData({ name: '' });
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
    loadUsers();
  };

  const openEdit = (clinic: AdminOrganization) => {
    setEditingClinic(clinic);
    setFormData({
      name: clinic.name,
      domain: clinic.domain ?? undefined,
      logoPath: clinic.logoPath ?? undefined,
      voicePhoneNumber: clinic.voicePhoneNumber ?? undefined,
      smsPhoneNumber: clinic.smsPhoneNumber ?? undefined,
      smsProvider: clinic.smsProvider ?? 'twilio',
      ringcentralCredentialKey: clinic.ringcentralCredentialKey ?? '',
      smsMessageTemplate: clinic.smsMessageTemplate ?? undefined,
      tellihealthWebhookToken: clinic.tellihealthWebhookToken ?? undefined,
      ownerUserId: clinic.ownerUserId ?? undefined,
      voiceEnabled: clinic.voiceEnabled ?? false,
      welcomeSmsEnabled: clinic.welcomeSmsEnabled ?? false,
      welcomeSmsTemplate: clinic.welcomeSmsTemplate ?? undefined,
      voiceOfPatientEnabled: clinic.voiceOfPatientEnabled ?? false,
      noTargetDatesEnabled: clinic.noTargetDatesEnabled ?? false,
      // PHA-1080: blank means "inherit" -- see the field's helper text.
      carePlanReviewMonths:
        clinic.carePlanReviewMonths == null ? '' : String(clinic.carePlanReviewMonths),
      hideSmartHubEnabled: clinic.hideSmartHubEnabled ?? false,
      smartHubAiEnabled: clinic.smartHubAiEnabled ?? false,
      ehrIntegration: clinic.ehrIntegrationOwn ?? false,
      ehrProvider: clinic.ehrProvider ?? '',
      cobaltCredentialKey: clinic.cobaltCredentialKey ?? '',
      // Only trust the stored nightly flag for a clinic that actually HAS an EHR
      // integration. 20260320_pha286_staging_deploy set run_nightly_import=true for every
      // org, so on a non-EHR clinic the box would come up pre-checked and enabling EHR
      // import would silently enroll it in the nightly refresh — reintroducing through the
      // UI exactly what the cron's `AND ehr_integration` guard exists to prevent.
      runNightlyImport: (clinic.ehrIntegrationOwn ?? false) ? (clinic.runNightlyImport ?? false) : false,
      // PHA-983: care-plan push settings.
      ehrCarePlanPushEnabled: clinic.ehrCarePlanPushEnabled ?? false,
      ehrCarePlanAutoPushEnabled: clinic.ehrCarePlanAutoPushEnabled ?? false,
      ehrDocumentFolderName: clinic.ehrDocumentFolderName ?? '',
      ehrDocumentLocationId: clinic.ehrDocumentLocationId ?? '',
      ehrDocumentAssignedToId: clinic.ehrDocumentAssignedToId ?? '',
      ehrDocumentIsReviewed: clinic.ehrDocumentIsReviewed ?? false,
      ehrDocumentHighPriority: clinic.ehrDocumentHighPriority ?? false,
      ehrDocumentTags: (clinic.ehrDocumentTags ?? []).join(', '),
      // PHA-1104: billing-report push settings.
      ehrBillingReportPushEnabled: clinic.ehrBillingReportPushEnabled ?? false,
      ehrBillingReportFolderName: clinic.ehrBillingReportFolderName ?? '',
    });
    setFormOpen(true);
    loadUsers();
    // PHA-958: an eCW clinic opens with a key already selected, so the options have to be
    // there for the Select to render it rather than showing a blank.
    if (clinic.ehrProvider === 'ecw') {
      loadCobaltKeys();
      if (clinic.cobaltCredentialKey) void loadCobaltDirectory(clinic.cobaltCredentialKey);
    }
    // PHA-1202: same for a RingCentral clinic's account and its sending numbers.
    setRcNumbers([]);
    setRcNumbersError(null);
    if (clinic.smsProvider === 'ringcentral') {
      loadRcAccounts();
      if (clinic.ringcentralCredentialKey) void loadRcNumbers(clinic.ringcentralCredentialKey);
    }
    // Load supported languages for this clinic
    setOrgLanguages([]);
    setLangTemplates({});
    organizationApi.getSupportedLanguages(clinic.id).then((data: any) => {
      if (Array.isArray(data)) {
        const codes = data.filter((l: any) => l.code !== 'en-US').map((l: any) => l.code);
        setOrgLanguages(codes);
        const templates: Record<string, string> = {};
        data.forEach((l: any) => { if (l.sms_message_template) templates[l.code] = l.sms_message_template; });
        setLangTemplates(templates);
      }
    }).catch(() => {});
    // PHA-723: load supported programs for this clinic
    setOrgPrograms([]);
    setOrgProgramsInherited(false);
    organizationApi.getSupportedPrograms(clinic.id).then((data: any) => {
      if (Array.isArray(data)) {
        setOrgPrograms(data.map((p: any) => p.program));
        // PHA-991: every row inherited => the clinic has no rows of its own yet.
        setOrgProgramsInherited(data.length > 0 && data.every((p: any) => p.inherited));
      }
    }).catch(() => {});
  };

  const handleLogoFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    if (!editingClinic) {
      toast.error('Save the clinic first, then upload a logo');
      return;
    }
    setLogoBusy(true);
    try {
      const { logo_path } = await adminApi.uploadOrganizationLogo(editingClinic.id, file);
      setFormData((prev) => ({ ...prev, logoPath: logo_path }));
      toast.success('Logo uploaded — save to apply');
    } catch (err: any) {
      toast.error(err.message || 'Failed to upload logo');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleLogoImportUrl = async () => {
    const sourceUrl = (formData.logoPath || '').trim();
    if (!sourceUrl) {
      toast.error('Enter an image URL first');
      return;
    }
    if (!editingClinic) {
      toast.error('Save the clinic first, then import a logo');
      return;
    }
    setLogoBusy(true);
    try {
      const { logo_path } = await adminApi.importOrganizationLogoFromUrl(editingClinic.id, sourceUrl);
      setFormData((prev) => ({ ...prev, logoPath: logo_path }));
      toast.success('Logo imported — save to apply');
    } catch (err: any) {
      toast.error(err.message || 'Failed to import logo from URL');
    } finally {
      setLogoBusy(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Clinic name is required');
      return;
    }

    // PHA-958: these combinations route a patient import at a real EHR, so block them
    // here as well as in the backend — a clinic saved as "EHR on, no provider" would show
    // the import button and then fail on click.
    const ehrIntegration = formData.ehrIntegration ?? false;
    const ehrProvider = ehrIntegration ? (formData.ehrProvider || '') : '';
    const cobaltCredentialKey = ehrProvider === 'ecw' ? (formData.cobaltCredentialKey || '') : '';
    if (ehrIntegration && !ehrProvider) {
      toast.error('Choose an EHR system (Athena or eClinicalWorks)');
      return;
    }
    if (ehrProvider === 'ecw' && !cobaltCredentialKey) {
      toast.error('Choose the Cobalt credentials for this clinic');
      return;
    }

    // PHA-983: care-plan push is Cobalt-only and needs somewhere to file. Validated here as
    // well as on the backend, so the admin is corrected in the form rather than a clinician
    // discovering it at send time.
    const pushEnabled = ehrProvider === 'ecw' && (formData.ehrCarePlanPushEnabled ?? false);
    const documentFolder = (formData.ehrDocumentFolderName || '').trim();
    if (pushEnabled && !documentFolder) {
      toast.error('Enter the EHR folder that care plans should be filed into');
      return;
    }

    // PHA-1104: the same three conditions for the billing report, checked independently —
    // a clinic can have one push on without the other.
    const billingPushEnabled =
      ehrProvider === 'ecw' && (formData.ehrBillingReportPushEnabled ?? false);
    const billingFolder = (formData.ehrBillingReportFolderName || '').trim();
    if (billingPushEnabled && !billingFolder) {
      toast.error('Enter the EHR folder that billing reports should be filed into');
      return;
    }

    // PHA-1080: care plan review cycle. Blank = 0 = the backend's clear-to-NULL sentinel,
    // which puts the clinic back to inheriting its parent network's cycle (or the 6-month
    // default when nothing in the chain sets one).
    //
    // Validated here rather than relying on the input's min/max: `type="number"` constraints
    // are advisory in a form that never calls checkValidity(), and a bad value would reach
    // the backend, raise, and surface as an opaque toast. The 1..120 range mirrors
    // _normalize_care_plan_review_months in app_logic.py — a 0-month cycle would produce a
    // review date of today, which the backend rejects as not in the future.
    const reviewMonthsRaw = (formData.carePlanReviewMonths ?? '').trim();
    let carePlanReviewMonthsPayload = 0;
    if (reviewMonthsRaw !== '') {
      const parsed = Number(reviewMonthsRaw);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 120) {
        toast.error('Care plan review cycle must be a whole number of months between 1 and 120, or blank to inherit');
        return;
      }
      carePlanReviewMonthsPayload = parsed;
    }

    // Location / staff / flags / tags describe the clinic's document desk, not the document
    // kind, so they survive as long as EITHER push is on.
    const anyPushEnabled = pushEnabled || billingPushEnabled;

    const ehrPayload = {
      ehr_integration: ehrIntegration,
      // '' clears the column server-side; the backend treats null as "leave unchanged".
      ehr_provider: ehrProvider,
      cobalt_credential_key: cobaltCredentialKey,
      run_nightly_import: ehrIntegration ? (formData.runNightlyImport ?? false) : false,
      ehr_care_plan_push_enabled: pushEnabled,
      // PHA-1204: only meaningful with push on; the backend refuses the contradiction.
      ehr_care_plan_auto_push_enabled: pushEnabled && (formData.ehrCarePlanAutoPushEnabled ?? false),
      ehr_document_folder_name: pushEnabled ? documentFolder : '',
      ehr_document_location_id: anyPushEnabled ? (formData.ehrDocumentLocationId || '') : '',
      ehr_document_assigned_to_id: anyPushEnabled ? (formData.ehrDocumentAssignedToId || '') : '',
      ehr_document_is_reviewed: anyPushEnabled ? (formData.ehrDocumentIsReviewed ?? false) : false,
      ehr_document_high_priority: anyPushEnabled ? (formData.ehrDocumentHighPriority ?? false) : false,
      ehr_document_tags: anyPushEnabled
        ? (formData.ehrDocumentTags || '').split(',').map((t) => t.trim()).filter(Boolean)
        : [],
      ehr_billing_report_push_enabled: billingPushEnabled,
      ehr_billing_report_folder_name: billingPushEnabled ? billingFolder : '',
    };

    // PHA-1202: send the SMS system only when it (or the RingCentral account) changes. The
    // backend validates a RingCentral choice live against RingCentral, so re-sending an
    // unchanged value would make every unrelated edit depend on RingCentral being reachable.
    const smsProvider: SmsProvider = formData.smsProvider ?? 'twilio';
    const rcKey = smsProvider === 'ringcentral' ? (formData.ringcentralCredentialKey || '') : '';
    if (smsProvider === 'ringcentral' && !rcKey) {
      toast.error('Choose the RingCentral account for this clinic');
      return;
    }
    if (smsProvider === 'ringcentral' && !(formData.smsPhoneNumber || '').trim()) {
      toast.error('Choose the RingCentral number this clinic sends from');
      return;
    }
    const smsSettingsChanged =
      smsProvider !== (editingClinic?.smsProvider ?? 'twilio')
      || rcKey !== (editingClinic?.smsProvider === 'ringcentral' ? (editingClinic?.ringcentralCredentialKey || '') : '');
    const smsProviderPayload = smsSettingsChanged
      ? { sms_provider: smsProvider, ringcentral_credential_key: rcKey }
      : {};

    setSaving(true);
    try {
      if (editingClinic) {
        await adminApi.updateOrganization(editingClinic.id, {
          name: formData.name,
          domain: formData.domain || null,
          logo_path: formData.logoPath || null,
          voice_phone_number: formData.voicePhoneNumber || null,
          sms_phone_number: formData.smsPhoneNumber || null,
          ...smsProviderPayload,
          sms_message_template: formData.smsMessageTemplate || null,
          tellihealth_webhook_token: formData.tellihealthWebhookToken || null,
          owner: formData.ownerUserId || null,
          voice_enabled: formData.voiceEnabled ?? false,
          welcome_sms_enabled: formData.welcomeSmsEnabled ?? false,
          welcome_sms_template: formData.welcomeSmsTemplate || null,
          voice_of_patient_enabled: formData.voiceOfPatientEnabled ?? false,
          no_target_dates_enabled: formData.noTargetDatesEnabled ?? false,
          hide_smart_hub_enabled: formData.hideSmartHubEnabled ?? false,
          smart_hub_ai_enabled: formData.smartHubAiEnabled ?? false,
          // PHA-1080: 0 is the backend's clear-to-NULL sentinel, so a blanked field puts the
          // clinic back on its network's cycle instead of silently leaving the old value.
          care_plan_review_months: carePlanReviewMonthsPayload,
          ...ehrPayload,
        });
        toast.success('Clinic updated');
      } else {
        await adminApi.createOrganization({
          name: formData.name,
          domain: formData.domain || null,
          logo_path: formData.logoPath || null,
          voice_phone_number: formData.voicePhoneNumber || null,
          sms_phone_number: formData.smsPhoneNumber || null,
          ...smsProviderPayload,
          sms_message_template: formData.smsMessageTemplate || null,
          tellihealth_webhook_token: formData.tellihealthWebhookToken || null,
          owner: formData.ownerUserId || null,
          voice_enabled: formData.voiceEnabled ?? false,
          welcome_sms_enabled: formData.welcomeSmsEnabled ?? false,
          welcome_sms_template: formData.welcomeSmsTemplate || null,
          voice_of_patient_enabled: formData.voiceOfPatientEnabled ?? false,
          no_target_dates_enabled: formData.noTargetDatesEnabled ?? false,
          hide_smart_hub_enabled: formData.hideSmartHubEnabled ?? false,
          smart_hub_ai_enabled: formData.smartHubAiEnabled ?? false,
          // PHA-1080: 0 (blank field) creates the clinic with NULL, i.e. inheriting its
          // parent network's cycle -- the right default for a brand-new clinic.
          care_plan_review_months: carePlanReviewMonthsPayload,
          ...ehrPayload,
        });
        toast.success('Clinic created');
      }
      setFormOpen(false);
      resetForm();
      loadClinics();
    } catch (err: any) {
      console.error('Failed to save clinic:', err);
      toast.error(err.message || 'Failed to save clinic');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget) return;
    try {
      await adminApi.updateOrganization(archiveTarget.id, { archived: true });
      toast.success('Clinic archived');
      setArchiveTarget(null);
      loadClinics();
    } catch (err: any) {
      console.error('Failed to archive clinic:', err);
      toast.error(err.message || 'Failed to archive clinic');
    }
  };

  return (
    <AdminLayout
      title="Clinics Management"
      subtitle="Manage organizations, owners, and activity totals."
    >
      <Card className="mb-4">
        <Box p="4">
          <Flex align="center" justify="between" gap="3" wrap="wrap">
            <TextField.Root style={{ minWidth: 240 }}>
              <TextField.Input
                placeholder="Search by clinic name, domain, or owner"
                value={search}
                onChange={(e) => setSearch(e.currentTarget?.value ?? '')}
              />
            </TextField.Root>
            <Flex align="center" gap="2">
              <Button variant="soft" onClick={loadClinics} disabled={loading}>
                <ReloadIcon className={loading ? 'animate-spin' : ''} />
                Refresh
              </Button>
              <Button variant="solid" onClick={openCreate}>
                <PlusIcon />
                Add Clinic
              </Button>
            </Flex>
          </Flex>
        </Box>
      </Card>

      <Card>
        <Box p="4">
          <Heading size="4" className="mb-3">All Clinics</Heading>
          {error && (
            <Text color="red" size="2" className="mb-3 block">{error}</Text>
          )}
          {loading && clinics.length === 0 ? (
            <Flex justify="center" align="center" style={{ minHeight: 120 }}>
              <ReloadIcon className="animate-spin" width="24" height="24" />
            </Flex>
          ) : (
            <Table.Root variant="surface">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Domain</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Owner</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>User Count</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Patient Count</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>EHR</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Voice AI</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Actions</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredClinics.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={8}>
                      <Text color="gray">No clinics match the current search.</Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  filteredClinics.map((clinic) => (
                    <Table.Row key={clinic.id}>
                      <Table.Cell>
                        <Text size="2" weight="medium">{clinic.name}</Text>
                        {clinic.archived && (
                          <Badge color="gray" className="ml-2">Archived</Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{clinic.domain || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{clinic.ownerEmail || '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{clinic.userCount ?? '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <Text size="2">{clinic.patientCount ?? '—'}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        {clinic.ehrIntegration ? (
                          <Badge color="green" variant="soft">Enabled</Badge>
                        ) : (
                          <Text size="2" color="gray">--</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {clinic.voiceEnabled ? (
                          <Badge color="blue" variant="soft">Enabled</Badge>
                        ) : (
                          <Text size="2" color="gray">--</Text>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Flex gap="2" wrap="wrap">
                          <Button variant="soft" onClick={() => openEdit(clinic)}>
                            <Pencil1Icon />
                            Edit
                          </Button>
                          <Button variant="soft" color="red" onClick={() => setArchiveTarget(clinic)}>
                            <ArchiveIcon />
                            Archive
                          </Button>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          )}
        </Box>
      </Card>

      <Dialog.Root
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) resetForm();
        }}
      >
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>{editingClinic ? 'Edit Clinic' : 'Add Clinic'}</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            {editingClinic ? 'Update clinic details and ownership.' : 'Create a new clinic for onboarding.'}
          </Dialog.Description>
          <Flex direction="column" gap="3" className="mt-4">
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Clinic Name</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="Clinic name"
                  value={formData.name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, name: e.currentTarget?.value ?? '' }))}
                />
              </TextField.Root>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Domain</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="clinic.example.com"
                  value={formData.domain || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, domain: e.currentTarget?.value ?? '' }))}
                />
              </TextField.Root>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Logo</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="https://example.com/logo.png"
                  value={formData.logoPath || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, logoPath: e.currentTarget?.value ?? '' }))}
                />
              </TextField.Root>
              <Flex gap="2" align="center" className="mt-2">
                <Button asChild variant="soft" size="1" disabled={logoBusy || !editingClinic}>
                  <label style={{ cursor: editingClinic ? 'pointer' : 'not-allowed' }}>
                    Upload file
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      style={{ display: 'none' }}
                      disabled={logoBusy || !editingClinic}
                      onChange={handleLogoFile}
                    />
                  </label>
                </Button>
                <Button
                  type="button"
                  variant="soft"
                  size="1"
                  disabled={logoBusy || !editingClinic || !(formData.logoPath || '').trim()}
                  onClick={handleLogoImportUrl}
                >
                  Import from URL
                </Button>
                {formData.logoPath ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img
                    src={formData.logoPath}
                    alt="Clinic logo preview"
                    style={{ maxHeight: 32, maxWidth: 120, objectFit: 'contain', borderRadius: 4 }}
                  />
                ) : null}
              </Flex>
              <Text size="1" color="gray" className="mt-1 block">
                {editingClinic
                  ? 'Upload a file or import from a URL — both are stored and served by Phanova so they always render. Recommended: PNG with transparent background, ~300×100 px. Appears on printed care plans.'
                  : 'Save the clinic first, then reopen it to upload or import a logo.'}
              </Text>
            </Box>
            <Box>
              {/* PHA-1202: per-clinic SMS system, laid out like the EHR section: Twilio (the
                  shared Phanova account, unchanged) or RingCentral, which needs the client's
                  own account and one of that account's SMS-enabled numbers. Phanova admins
                  only — the clinic's Clinic Profile page shows it read-only. */}
              <Text size="2" weight="medium" className="mb-1 block">SMS system</Text>
              <Select.Root
                value={formData.smsProvider ?? 'twilio'}
                onValueChange={(value) => {
                  const provider = value as SmsProvider;
                  setFormData((prev) => ({
                    ...prev,
                    smsProvider: provider,
                    ringcentralCredentialKey: provider === 'ringcentral' ? prev.ringcentralCredentialKey : '',
                  }));
                  if (provider === 'ringcentral') {
                    loadRcAccounts();
                    if (formData.ringcentralCredentialKey) void loadRcNumbers(formData.ringcentralCredentialKey);
                  }
                }}
              >
                <Select.Trigger />
                <Select.Content position="popper">
                  {SMS_PROVIDER_OPTIONS.map((option) => (
                    <Select.Item key={option.value} value={option.value}>{option.label}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Text size="1" color="gray" className="mt-1 block">
                Every text this clinic sends, and every reply a patient texts back (STOP / START), goes through this system.
              </Text>

              {formData.smsProvider === 'ringcentral' && (
                <Box className="mt-3 pl-6">
                  <Text size="2" weight="medium" className="mb-1 block">RingCentral account</Text>
                  {rcAccountsLoading ? (
                    <Text size="1" color="gray">Loading accounts…</Text>
                  ) : (
                    <Select.Root
                      value={formData.ringcentralCredentialKey || ''}
                      onValueChange={(value) => {
                        // A number belongs to ONE account, so the old one cannot carry over.
                        setFormData((prev) => ({ ...prev, ringcentralCredentialKey: value, smsPhoneNumber: '' }));
                        void loadRcNumbers(value);
                      }}
                    >
                      <Select.Trigger placeholder="Choose the RingCentral account for this clinic" />
                      <Select.Content position="popper">
                        {rcAccounts.map(({ key, configured }) => (
                          <Select.Item key={key} value={key} disabled={!configured}>
                            {key}{configured ? '' : ' (credentials not filled in yet)'}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                  )}
                  <Text size="1" color="gray" className="mt-1 block">
                    Each account is a client's own RingCentral, stored in AWS Secrets Manager (prod/sms/ringcentral).
                  </Text>
                  {rcAccountsError && (
                    <Text size="1" color="red" className="mt-1 block">
                      Could not read the RingCentral accounts: {rcAccountsError}
                    </Text>
                  )}
                  {!rcAccountsLoading && !rcAccountsError && rcAccountsLoaded && rcAccounts.length === 0 && (
                    <Text size="1" color="orange" className="mt-1 block">
                      No RingCentral accounts are provisioned yet.
                    </Text>
                  )}
                </Box>
              )}
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">SMS Phone Number</Text>
              {formData.smsProvider === 'ringcentral' ? (
                rcNumbersLoading ? (
                  <Text size="1" color="gray">Loading the account's numbers…</Text>
                ) : (
                  <Select.Root
                    value={formData.smsPhoneNumber || ''}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, smsPhoneNumber: value }))}
                    disabled={!formData.ringcentralCredentialKey}
                  >
                    <Select.Trigger placeholder={formData.ringcentralCredentialKey ? 'Choose the number texts are sent from' : 'Choose the account first'} />
                    <Select.Content position="popper">
                      {/* Keep a stored number visible even when it is not (or no longer) on the
                          account, so the admin sees what is saved; the backend rejects it on save. */}
                      {formData.smsPhoneNumber
                        && !rcNumbers.some((n) => n.phone_number.replace(/\D/g, '').slice(-10) === (formData.smsPhoneNumber || '').replace(/\D/g, '').slice(-10)) && (
                        <Select.Item value={formData.smsPhoneNumber} disabled>
                          {formData.smsPhoneNumber} (not an SMS number on this account)
                        </Select.Item>
                      )}
                      {rcNumbers.map((n) => (
                        <Select.Item key={n.phone_number} value={n.phone_number}>
                          {n.phone_number}{n.usage_type === 'MainCompanyNumber' ? ' — main company number' : ''}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                )
              ) : (
                <TextField.Root>
                  <TextField.Input
                    placeholder="+12125551234 or 2125551234"
                    value={formData.smsPhoneNumber || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, smsPhoneNumber: e.currentTarget?.value ?? '' }))}
                  />
                </TextField.Root>
              )}
              <Text size="1" color="gray" className="mt-1 block">
                {formData.smsProvider === 'ringcentral'
                  ? "The number texts are sent from. RingCentral only sends from SMS-enabled numbers of the account's own user, which is what this list shows."
                  : "The number texts are sent from. Must be a number on the selected provider's account."}
              </Text>
              {formData.smsProvider === 'ringcentral' && rcNumbersError && (
                <Text size="1" color="red" className="mt-1 block">
                  Could not read the account's numbers: {rcNumbersError}
                </Text>
              )}
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">SMS Message Template</Text>
              <TextArea
                placeholder="Phanova: Your care team at the clinic asked us to check in with you as part of your Chronic Care Management program: {link}"
                value={formData.smsMessageTemplate || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, smsMessageTemplate: e.currentTarget?.value ?? '' }))}
                rows={3}
              />
              <Text size="1" color="gray" className="mt-1 block">
                Use {'{link}'} for the survey URL. Leave blank for default.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Welcome SMS</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.welcomeSmsEnabled ?? false}
                  onChange={(e) => setFormData((prev) => ({ ...prev, welcomeSmsEnabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Text size="2">Send welcome SMS when patient consents</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                When enabled, a welcome SMS is sent after the patient completes onboarding consent.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Welcome SMS Template (optional)</Text>
              <TextArea
                placeholder={`Hi {first_name}, you've been enrolled in the Chronic Care Program through your clinic, {clinic_name}. We'll be in touch soon to support your care.`}
                value={formData.welcomeSmsTemplate || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, welcomeSmsTemplate: e.currentTarget?.value ?? '' }))}
                rows={3}
              />
              <Text size="1" color="gray" className="mt-1 block">
                Customize the welcome message. Use {'{first_name}'} and {'{clinic_name}'} as placeholders.
                Leave blank to use the default message.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Voice AI Phone Number</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="+12125551234 or 2125551234"
                  value={formData.voicePhoneNumber || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, voicePhoneNumber: e.currentTarget?.value ?? '' }))}
                />
              </TextField.Root>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Voice AI</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.voiceEnabled ?? false}
                  onChange={(e) => setFormData((prev) => ({ ...prev, voiceEnabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Text size="2">Enable Voice AI contact method for this clinic</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                When enabled, patients can be contacted via automated Voice AI phone calls in addition to SMS.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">PCMH Compliant</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.voiceOfPatientEnabled ?? false}
                  onChange={(e) => setFormData((prev) => ({ ...prev, voiceOfPatientEnabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Text size="2">Enable Voice of the Patient, full list of problems and meds in the care-plan, care plan review date, etc.</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                Marks this clinic as PCMH-compliant. Enables the Voice-of-the-Patient tab and person-centered intake (goals, barriers, GAS) on each patient's chart.
              </Text>
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">No target dates on goals &amp; tasks</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.noTargetDatesEnabled ?? false}
                  onChange={(e) => setFormData((prev) => ({ ...prev, noTargetDatesEnabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Text size="2">Hide target dates on care-plan goals and tasks.</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                When enabled, goals and tasks show "Due: N/A", the care-plan PDF drops the Due Date / Achieved / Achievement Date columns, and the clinic's tasks are excluded from SmartHub monitoring. Existing due dates are hidden, not deleted.
              </Text>
            </Box>
            {/* PHA-1080: the review cycle behind the "Care Plan Review Date" on the patient
                list, the plan header and the printed plan. Six months is the PCMH
                recommendation; CCM-style long-term management clinics run twelve. */}
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Care plan review cycle</Text>
              <Flex align="center" gap="2">
                <TextField.Root style={{ maxWidth: 110 }}>
                  <TextField.Input
                    type="number"
                    min={1}
                    max={120}
                    step={1}
                    placeholder="6"
                    value={formData.carePlanReviewMonths ?? ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, carePlanReviewMonths: e.target.value }))}
                  />
                </TextField.Root>
                <Text size="2" color="gray">months</Text>
              </Flex>
              <Text size="1" color="gray" className="mt-1 block">
                How far ahead the next care plan is due when a plan is approved. Leave blank to
                inherit from the parent network, or to use the 6-month default if there is none.
                Applies to plans approved from now on — review dates already recorded on
                existing plans are not changed.
              </Text>
            </Box>
            {/* PHA-1031: clinics that don't collect data through Phanova get no value
                from the Smart Hub feed — hide the rail for them entirely. */}
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Hide Smart Hub</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.hideSmartHubEnabled ?? false}
                  onChange={(e) => setFormData((prev) => ({ ...prev, hideSmartHubEnabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Text size="2">Hide the Smart Hub rail for this clinic.</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                For clinics that don't collect patient data through Phanova, where the Smart Hub feed and its AI insights have nothing to show. Hide-only: tasks, acknowledgements and insights are untouched, so unchecking this brings the rail back.
              </Text>
            </Box>
            {/* PHA-1168: Custom Views rollout gate. Saved immediately through its own
                endpoint (not the form's PUT), so it can never break clinic editing on a
                schema that predates the migration. */}
            {editingClinic?.id && <CustomViewsToggle organizationId={String(editingClinic.id)} />}
            {/* PHA-1184: per-clinic AI insights rollout. Gates the Smart Hub insights band,
                the patient AI Insights tab, and nightly / EHR-refresh insight generation. */}
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">AI Insights</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.smartHubAiEnabled ?? false}
                  onChange={(e) => setFormData((prev) => ({ ...prev, smartHubAiEnabled: e.target.checked }))}
                  className="w-4 h-4"
                />
                <Text size="2">Enable AI insights for this clinic.</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                Shows the AI insights band in the Smart Hub and the AI Insights tab on patient charts, and includes the clinic in nightly insight generation. Turning it off hides insights and stops generating new ones; existing insights are kept. Hide Smart Hub, when checked, hides insights regardless of this setting.
              </Text>
            </Box>
            {/* PHA-958: per-clinic EHR integration — drives the "Import from EHR" button on
                the new-patient page, the admin EHR Sync trigger, and the nightly refresh. */}
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">EHR Integration</Text>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.ehrIntegration ?? false}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    ehrIntegration: e.target.checked,
                    // Turning it off clears the routing so a later re-enable can't inherit
                    // a stale provider or credential mapping.
                    ...(e.target.checked ? {} : { ehrProvider: '' as const, cobaltCredentialKey: '', runNightlyImport: false }),
                  }))}
                  className="w-4 h-4"
                />
                <Text size="2">Enable EHR import for this clinic</Text>
              </label>
              <Text size="1" color="gray" className="mt-1 block">
                When enabled, staff can import a patient by MRN from the clinic's EHR on the new-patient page, and the clinic becomes available to the admin EHR Sync tool.
              </Text>

              {(formData.ehrIntegration ?? false) && (
                <Box className="mt-3 pl-6">
                  <Text size="2" weight="medium" className="mb-1 block">EHR system</Text>
                  <Select.Root
                    value={formData.ehrProvider || ''}
                    onValueChange={(value) => {
                      const provider = value as EhrProvider;
                      setFormData((prev) => ({
                        ...prev,
                        ehrProvider: provider,
                        // Athena authenticates via its own credentials + organization_practice,
                        // so drop any Cobalt key left over from an earlier eCW selection.
                        cobaltCredentialKey: provider === 'ecw' ? prev.cobaltCredentialKey : '',
                      }));
                      if (provider === 'ecw') loadCobaltKeys();
                    }}
                  >
                    <Select.Trigger placeholder="Choose the clinic's EHR" />
                    {/* "popper" anchors the popup to the trigger and is collision-aware,
                        which suits a Select that opens with no value inside a scrolled
                        dialog. NOTE: this is NOT what fixed the "dropdown opens empty" bug
                        — that was a z-index problem (see the :has() rule in index.css);
                        the popup was painting behind the dialog. Kept because it is the
                        better positioning mode here, not as the fix. */}
                    <Select.Content position="popper">
                      {EHR_PROVIDER_OPTIONS.map((option) => (
                        <Select.Item key={option.value} value={option.value}>{option.label}</Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>

                  {formData.ehrProvider === 'athena' && (
                    <Text size="1" color="gray" className="mt-1 block">
                      Athena clinics also need a row in <code>organization_practice</code> for their practice ID.
                    </Text>
                  )}

                  {formData.ehrProvider === 'ecw' && (
                    <Box className="mt-3">
                      <Text size="2" weight="medium" className="mb-1 block">Cobalt credentials</Text>
                      {cobaltKeysLoading ? (
                        <Text size="1" color="gray">Loading credentials…</Text>
                      ) : (
                        <Select.Root
                          value={formData.cobaltCredentialKey || ''}
                          onValueChange={(value) => {
                            setFormData((prev) => ({
                              ...prev,
                              cobaltCredentialKey: value,
                              // PHA-983: the location/staff ids belong to the OLD Cobalt
                              // account and mean nothing under the new one. Clearing them
                              // is what stops a stale id being saved and silently filing
                              // into another clinic's facility.
                              ehrDocumentLocationId: '',
                              ehrDocumentAssignedToId: '',
                            }));
                            void loadCobaltDirectory(value);
                          }}
                        >
                          <Select.Trigger placeholder="Choose the Cobalt account for this clinic" />
                          {/* Same reason as the EHR system Select above — this one also
                              opens with no value selected. */}
                          <Select.Content position="popper">
                            {cobaltKeys.map(({ key, ehr }) => {
                              // A Cobalt account backed by another EHR cannot serve this
                              // clinic — Cobalt rejects the fetch. Disable rather than let
                              // it be saved and fail later at import time.
                              const mismatched = ehr !== null && ehr !== 'ecw';
                              const label = ehr === null
                                ? `${key} — EHR unknown`
                                : `${key} — ${EHR_PROVIDER_OPTIONS.find((o) => o.value === ehr)?.label ?? ehr}`;
                              return (
                                <Select.Item key={key} value={key} disabled={mismatched}>
                                  {label}{mismatched ? ' (not eClinicalWorks)' : ''}
                                </Select.Item>
                              );
                            })}
                          </Select.Content>
                        </Select.Root>
                      )}
                      <Text size="1" color="gray" className="mt-1 block">
                        eClinicalWorks is reached through Cobalt. Each Cobalt account is connected to one EHR, so only accounts connected to eClinicalWorks can serve this clinic — the rest are shown greyed out. The names come from Cobalt and need not match the clinic name here.
                      </Text>
                      {!cobaltKeysLoading && cobaltKeys.length > 0
                        && cobaltKeys.every((a) => a.ehr !== null && a.ehr !== 'ecw') && (
                        <Text size="1" color="orange" className="mt-1 block">
                          None of the available Cobalt accounts are connected to eClinicalWorks, so this clinic cannot import yet. Its own Cobalt account needs to be provisioned first.
                        </Text>
                      )}
                      {!cobaltKeysLoading && cobaltKeys.some((a) => a.ehr === null) && (
                        <Text size="1" color="gray" className="mt-1 block">
                          Accounts marked “EHR unknown” have no EHR recorded in the shared credentials, so they cannot be checked here — confirm the account is an eClinicalWorks one before selecting it.
                        </Text>
                      )}
                      {cobaltKeysError && (
                        <Text size="1" color="red" className="mt-1 block">
                          Could not read the Cobalt credentials: {cobaltKeysError}
                        </Text>
                      )}
                      {!cobaltKeysLoading && !cobaltKeysError && cobaltKeys.length === 0 && (
                        <Text size="1" color="orange" className="mt-1 block">
                          No credentials are provisioned yet. This clinic has to be added to the shared Cobalt credentials before its import will work.
                        </Text>
                      )}
                    </Box>
                  )}

                  <Box className="mt-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.runNightlyImport ?? false}
                        onChange={(e) => setFormData((prev) => ({ ...prev, runNightlyImport: e.target.checked }))}
                        className="w-4 h-4"
                      />
                      <Text size="2">Run the daily EHR refresh for this clinic</Text>
                    </label>
                    <Text size="1" color="gray" className="mt-1 block">
                      Re-pulls each imported patient from the EHR nightly and surfaces the changes. Leave off to keep imports manual.
                    </Text>
                  </Box>

                  {/* PHA-983: push approved care plans back into the EHR. eCW only — it is
                      delivered through Cobalt's POST /documents, which Athena clinics do not
                      reach. A separate opt-in from import: writing into a chart is a bigger
                      commitment than reading from one. */}
                  {formData.ehrProvider === 'ecw' && (
                    <Box className="mt-4 pt-3 border-t flex flex-col gap-3">
                      {/* PHA-1105: each of the three sections below is framed and labelled.
                          Unframed they read as one column of checkboxes and inputs, which is
                          how the billing folder ends up typed into the care-plan field. */}
                      <Box className="p-3" style={{ border: '1px solid var(--gray-a6)', borderRadius: 'var(--radius-3)' }}>
                        <Text size="1" weight="bold" color="gray" className="mb-2 block uppercase tracking-wide">Care plans</Text>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.ehrCarePlanPushEnabled ?? false}
                            onChange={(e) => setFormData((prev) => ({
                              ...prev,
                              ehrCarePlanPushEnabled: e.target.checked,
                              // Seed the default folder on enable, but never overwrite a
                              // folder someone has already chosen — including on a re-tick
                              // after an accidental untick.
                              ehrDocumentFolderName: e.target.checked
                                ? (prev.ehrDocumentFolderName || DEFAULT_EHR_DOCUMENT_FOLDER)
                                : prev.ehrDocumentFolderName,
                            }))}
                            className="w-4 h-4"
                          />
                          <Text size="2" weight="medium">Send care plans to EHR</Text>
                        </label>
                        <Text size="1" color="gray" className="mt-1 block">
                          Adds a “Push Care Plan to EMR” button to the patient chart, under the care plan tab, which files the approved care plan PDF into the folder below.
                          {(formData.ehrCarePlanAutoPushEnabled ?? false) ? '' : ' Nothing is sent automatically.'}
                        </Text>

                        {/* PHA-1204: automatic upload is a mode of the push above, so it only
                            appears once push is on (and is cleared on save when push is off). */}
                        {(formData.ehrCarePlanPushEnabled ?? false) && (
                          <Box className="mt-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.ehrCarePlanAutoPushEnabled ?? false}
                                onChange={(e) => setFormData((prev) => ({
                                  ...prev,
                                  ehrCarePlanAutoPushEnabled: e.target.checked,
                                }))}
                                className="w-4 h-4"
                              />
                              <Text size="2" weight="medium">Automatically upload new care plans to EHR</Text>
                            </label>
                            <Text size="1" color="gray" className="mt-1 block">
                              When a new care plan is approved — at the end of onboarding or after a regeneration — it is uploaded to the folder below right away, without pressing the button.
                            </Text>
                          </Box>
                        )}

                        {(formData.ehrCarePlanPushEnabled ?? false) && (
                          <Box className="mt-3">
                            <Text size="2" weight="medium" className="mb-1 block">Care Plans Destination Folder</Text>
                            <TextField.Root>
                              <TextField.Input
                                placeholder={DEFAULT_EHR_DOCUMENT_FOLDER}
                                value={formData.ehrDocumentFolderName || ''}
                                onChange={(e) => setFormData((prev) => ({ ...prev, ehrDocumentFolderName: e.target.value }))}
                              />
                            </TextField.Root>
                            <Text size="1" color="gray" className="mt-1 block">
                              Must match a folder that already exists in the EHR — Cobalt does not create one. Nested paths use forward slashes. Defaults to “{DEFAULT_EHR_DOCUMENT_FOLDER}”; change it if this clinic files these somewhere else.
                            </Text>
                          </Box>
                        )}
                      </Box>

                      {/* PHA-1104 / PHA-1105: the monthly billing report, filed the same way
                          into a folder of its own. Its own opt-in because a billing audit
                          trail is not a clinical document — a clinic that wants care plans
                          in its charts has not thereby asked for billing reports. */}
                      <Box className="p-3" style={{ border: '1px solid var(--gray-a6)', borderRadius: 'var(--radius-3)' }}>
                        <Text size="1" weight="bold" color="gray" className="mb-2 block uppercase tracking-wide">Billing reports</Text>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.ehrBillingReportPushEnabled ?? false}
                            onChange={(e) => setFormData((prev) => ({
                              ...prev,
                              ehrBillingReportPushEnabled: e.target.checked,
                            }))}
                            className="w-4 h-4"
                          />
                          <Text size="2" weight="medium">Send Billing Reports to EHR</Text>
                        </label>
                        <Text size="1" color="gray" className="mt-1 block">
                          Adds a “Push Billing Report to EMR” button to the billing report page, to the left of “Export Summary CSV”, which files a single patient’s billing summary and audit trail into the folder below. It appears only for a single-patient report. Nothing is sent automatically.
                        </Text>

                        {(formData.ehrBillingReportPushEnabled ?? false) && (
                          <Box className="mt-3">
                            <Text size="2" weight="medium" className="mb-1 block">Phanova Reports Destination Folder</Text>
                            <TextField.Root>
                              <TextField.Input
                                placeholder="e.g. Phanova Reports"
                                value={formData.ehrBillingReportFolderName || ''}
                                onChange={(e) => setFormData((prev) => ({ ...prev, ehrBillingReportFolderName: e.target.value }))}
                              />
                            </TextField.Root>
                            <Text size="1" color="gray" className="mt-1 block">
                              Must match a folder that already exists in the EHR — Cobalt does not create one. Nested paths use forward slashes. Deliberately has no default: there is no folder every eCW clinic already has for these, and guessing one is what made every care-plan push fail with “folder not found” (PHA-983). Ask the clinic which folder to use.
                            </Text>
                          </Box>
                        )}
                      </Box>

                      {/* The clinic's document desk — which facility, which staff member,
                          how documents are flagged. Shared by both pushes on purpose: these
                          describe where documents land in the EHR, not what kind of document
                          it is, so they show whenever either push is on. */}
                      {((formData.ehrCarePlanPushEnabled ?? false)
                        || (formData.ehrBillingReportPushEnabled ?? false)) && (
                        <Box className="p-3 flex flex-col gap-3" style={{ border: '1px solid var(--gray-a6)', borderRadius: 'var(--radius-3)' }}>
                          <Text size="1" weight="bold" color="gray" className="uppercase tracking-wide">Document filing — applies to both</Text>
                          {cobaltLookupError && (
                            <Text size="1" color="orange">
                              Could not read the EHR directory ({cobaltLookupError}). You can still enter the ids by hand below.
                            </Text>
                          )}

                          <Box>
                            <Text size="2" weight="medium" className="mb-1 block">Location</Text>
                            {cobaltLookupLoading ? (
                              <Text size="1" color="gray">Loading locations…</Text>
                            ) : cobaltLocations.length > 0 ? (
                              <Select.Root
                                value={formData.ehrDocumentLocationId || ''}
                                onValueChange={(value) => setFormData((prev) => ({ ...prev, ehrDocumentLocationId: value }))}
                              >
                                <Select.Trigger placeholder="Choose the facility documents are listed under" />
                                <Select.Content position="popper">
                                  {/* PHA-983: the value is the EMR id (ehr_id), NOT Cobalt's own id.
                                      Confirmed live 2026-08-13 — sending the Cobalt id gets
                                      400 "Location with EMR ID '<cobalt id>' not found". */}
                                  {cobaltLocations.filter((loc) => loc.ehr_id || loc.id).map((loc) => (
                                    <Select.Item key={loc.id} value={String(loc.ehr_id || loc.id)}>
                                      {loc.name}{loc.status && loc.status !== 'active' ? ` (${loc.status})` : ''}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                            ) : (
                              <TextField.Root>
                                <TextField.Input
                                  placeholder="EMR location id (e.g. 4)"
                                  value={formData.ehrDocumentLocationId || ''}
                                  onChange={(e) => setFormData((prev) => ({ ...prev, ehrDocumentLocationId: e.target.value }))}
                                />
                              </TextField.Root>
                            )}
                          </Box>

                          <Box>
                            <Text size="2" weight="medium" className="mb-1 block">Listed under (staff member)</Text>
                            {cobaltLookupLoading ? (
                              <Text size="1" color="gray">Loading staff…</Text>
                            ) : cobaltProviders.length > 0 ? (
                              <Select.Root
                                value={formData.ehrDocumentAssignedToId || ''}
                                onValueChange={(value) => setFormData((prev) => ({ ...prev, ehrDocumentAssignedToId: value }))}
                              >
                                <Select.Trigger placeholder="Choose who the document is listed under" />
                                <Select.Content position="popper">
                                  {/* PHA-983: EMR id again — the Cobalt field is named
                                      emr_assigned_to_id, and location_id was confirmed to work
                                      the same way. */}
                                  {cobaltProviders.filter((prov) => prov.ehr_id || prov.id).map((prov) => (
                                    <Select.Item key={prov.id} value={String(prov.ehr_id || prov.id)}>
                                      {prov.name}{prov.status && prov.status !== 'active' ? ` (${prov.status})` : ''}
                                    </Select.Item>
                                  ))}
                                </Select.Content>
                              </Select.Root>
                            ) : (
                              <TextField.Root>
                                <TextField.Input
                                  placeholder="EMR staff id"
                                  value={formData.ehrDocumentAssignedToId || ''}
                                  onChange={(e) => setFormData((prev) => ({ ...prev, ehrDocumentAssignedToId: e.target.value }))}
                                />
                              </TextField.Root>
                            )}
                            <Text size="1" color="gray" className="mt-1 block">
                              Staff are listed whatever their status — an “inactive” marking in the EHR does not stop documents being filed under them.
                            </Text>
                          </Box>

                          <Box className="flex flex-col gap-2">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.ehrDocumentIsReviewed ?? false}
                                onChange={(e) => setFormData((prev) => ({ ...prev, ehrDocumentIsReviewed: e.target.checked }))}
                                className="w-4 h-4"
                              />
                              <Text size="2">Mark documents as already reviewed</Text>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={formData.ehrDocumentHighPriority ?? false}
                                onChange={(e) => setFormData((prev) => ({ ...prev, ehrDocumentHighPriority: e.target.checked }))}
                                className="w-4 h-4"
                              />
                              <Text size="2">Mark documents as high priority</Text>
                            </label>
                          </Box>

                          <Box>
                            <Text size="2" weight="medium" className="mb-1 block">Tags (optional)</Text>
                            <TextField.Root>
                              <TextField.Input
                                placeholder="Comma-separated, e.g. care-plan, phanova"
                                value={formData.ehrDocumentTags || ''}
                                onChange={(e) => setFormData((prev) => ({ ...prev, ehrDocumentTags: e.target.value }))}
                              />
                            </TextField.Root>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">TelliHealth Webhook Token</Text>
              <TextField.Root>
                <TextField.Input
                  placeholder="Paste TelliHealth webhook token (leave empty if not used)"
                  value={formData.tellihealthWebhookToken || ''}
                  onChange={(e) => setFormData((prev) => ({ ...prev, tellihealthWebhookToken: e.currentTarget?.value ?? '' }))}
                />
              </TextField.Root>
              <Text size="1" color="gray" className="mt-1 block">
                Token used to authenticate incoming webhook requests. Leave empty to disable.
              </Text>
              {editingClinic && formData.tellihealthWebhookToken && (
                <Text size="1" color="gray" className="mt-1 block">
                  Webhook URL: /webhooks/tellihealth/{encodeURIComponent(formData.name)}/readings
                </Text>
              )}
            </Box>
            {editingClinic && (
              <Box>
                <Text size="2" weight="medium" className="mb-2 block">Supported Programs</Text>
                <Text size="1" color="gray" className="mb-2 block">
                  This clinic bills and displays only the programs enabled here. At least one is required.
                </Text>
                {orgProgramsInherited && (
                  <Text size="1" color="gray" className="mb-2 block">
                    Not configured yet — showing the defaults this clinic currently bills under.
                    Changing any toggle saves this selection.
                  </Text>
                )}
                <Flex direction="column" gap="2">
                  {ALL_PROGRAMS.map((prog) => {
                    const isEnabled = orgPrograms.includes(prog.code);
                    return (
                      <Flex key={prog.code} align="center" gap="2">
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={async (checked) => {
                            if (!checked && orgPrograms.length <= 1) {
                              toast.error('A clinic must support at least one program');
                              return;
                            }
                            try {
                              if (checked) {
                                await organizationApi.upsertSupportedProgram(prog.code, editingClinic.id);
                                setOrgPrograms((prev) => [...prev, prog.code]);
                                toast.success(`${prog.name} enabled`);
                              } else {
                                const result = await organizationApi.deleteSupportedProgram(prog.code, editingClinic.id);
                                if (result && result.error) {
                                  toast.error(result.error);
                                  return;
                                }
                                setOrgPrograms((prev) => prev.filter((c) => c !== prog.code));
                                toast.success(`${prog.name} disabled`);
                              }
                            } catch (err) {
                              toast.error(`Failed to update ${prog.name}`);
                            }
                          }}
                        />
                        <Text size="2">{prog.name} — {prog.description}</Text>
                      </Flex>
                    );
                  })}
                </Flex>
              </Box>
            )}
            {editingClinic && (
              <Box>
                <Text size="2" weight="medium" className="mb-2 block">Supported Patient Languages</Text>
                <Text size="1" color="gray" className="mb-2 block">
                  English is always available. Toggle additional languages for this clinic's patients.
                </Text>
                <Flex direction="column" gap="2">
                  {ALL_LANGUAGES.map((lang) => {
                    const isEnabled = orgLanguages.includes(lang.code);
                    return (
                      <Flex key={lang.code} direction="column" gap="1">
                        <Flex align="center" gap="2">
                          <Switch
                            checked={isEnabled}
                            onCheckedChange={async (checked) => {
                              try {
                                if (checked) {
                                  await organizationApi.upsertSupportedLanguage(lang.code, undefined, editingClinic.id);
                                  setOrgLanguages((prev) => [...prev, lang.code]);
                                  toast.success(`${lang.name} enabled`);
                                } else {
                                  await organizationApi.deleteSupportedLanguage(lang.code, editingClinic.id);
                                  setOrgLanguages((prev) => prev.filter((c) => c !== lang.code));
                                  setLangTemplates((prev) => { const next = { ...prev }; delete next[lang.code]; return next; });
                                  toast.success(`${lang.name} disabled`);
                                }
                              } catch (err) {
                                toast.error(`Failed to update ${lang.name}`);
                              }
                            }}
                          />
                          <Text size="2">{lang.name} ({lang.native_name})</Text>
                        </Flex>
                        {isEnabled && (
                          <TextArea
                            placeholder={`SMS template in ${lang.name} (must include {link})`}
                            value={langTemplates[lang.code] || ''}
                            onChange={(e) => setLangTemplates((prev) => ({ ...prev, [lang.code]: e.currentTarget?.value ?? '' }))}
                            onBlur={async () => {
                              const tmpl = langTemplates[lang.code];
                              if (tmpl && !tmpl.includes('{link}')) {
                                toast.error(`${lang.name} SMS template must include {link}`);
                                return;
                              }
                              try {
                                await organizationApi.upsertSupportedLanguage(lang.code, tmpl || undefined, editingClinic.id);
                              } catch (err) {
                                toast.error(`Failed to save ${lang.name} template`);
                              }
                            }}
                            rows={2}
                          />
                        )}
                      </Flex>
                    );
                  })}
                </Flex>
              </Box>
            )}
            <Box>
              <Text size="2" weight="medium" className="mb-1 block">Owner</Text>
              <Select.Root
                value={formData.ownerUserId || 'none'}
                onValueChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    ownerUserId: value === 'none' ? undefined : value,
                  }))
                }
              >
                <Select.Trigger placeholder="Select owner" />
                <Select.Content>
                  <Select.Item value="none">No owner</Select.Item>
                  {users.map((user) => (
                    <Select.Item key={user.id} value={user.id}>
                      {user.email}{user.name ? ` (${user.name})` : ''}
                    </Select.Item>
                  ))}
                  {usersLoading && (
                    <Select.Item value="loading" disabled>
                      Loading users...
                    </Select.Item>
                  )}
                </Select.Content>
              </Select.Root>
            </Box>
          </Flex>
          <Flex justify="end" gap="2" className="mt-4">
            <Dialog.Close>
              <Button variant="soft" type="button">Cancel</Button>
            </Dialog.Close>
            <Button variant="solid" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Clinic'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AlertDialog.Root open={!!archiveTarget} onOpenChange={(open) => !open && setArchiveTarget(null)}>
        <AlertDialog.Content style={{ maxWidth: 420 }}>
          <AlertDialog.Title>Archive Clinic</AlertDialog.Title>
          <AlertDialog.Description size="2" color="gray">
            Archive {archiveTarget?.name}? This will hide the clinic from active lists.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" className="mt-4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button color="red" onClick={handleArchive}>Archive</Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </AdminLayout>
  );
};

export default AdminClinicsPage;
